import React, { useState } from "react";
import { Badge, Tooltip, Button } from "flowbite-react";
import {
  HiBuildingOffice2,
  HiCalendarDays,
  HiCurrencyDollar,
  HiChevronDown,
  HiChevronUp,
  HiArrowTopRightOnSquare,
  HiMapPin,
} from "react-icons/hi2";
import TitleStripe from "../../common/TitleStripe";
import NoOcurrence from "../../common/query/NoOcurrence";
import Emails from "../../common/query/Emails";
import Telefones from "../../common/query/Telefones";
import Sociedade from "../../common/query/Sociedade";
import { formatDocument, formatToReal } from "../../../utils/utils";
import moment from "moment";
import { HiClipboard } from "react-icons/hi";

const DadosReceitaFederalPJ = ({ data }) => {
  const pj = data?.dadosCadastrais || data;
  const [showMore, setShowMore] = useState(false);

  // ===== Endereço (helpers locais) =====
  const cepFmt = String(pj?.cep || "")
    .replace(/\D/g, "")
    .replace(/^(\d{5})(\d{0,3}).*/, (_, a, b) => (b ? `${a}-${b}` : a));

  const addressLine = [
    pj?.logradouro || pj?.endereco,
    pj?.numero,
    pj?.complemento,
  ]
    .filter(Boolean)
    .join(", ");

  const cityLine = [
    pj?.bairro && pj?.bairro,
    (pj?.cidade || pj?.municipio) &&
      (pj?.uf
        ? `${pj?.cidade || pj?.municipio}/${pj?.uf}`
        : pj?.cidade || pj?.municipio),
  ]
    .filter(Boolean)
    .join(" • ");

  const fullAddress = [addressLine, cityLine, cepFmt && `CEP ${cepFmt}`]
    .filter(Boolean)
    .join(" • ");

  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    fullAddress
  )}`;

  async function handleCopyAddress() {
    try {
      await navigator.clipboard.writeText(fullAddress || "");
    } catch {}
  }

  if (!pj) return <NoOcurrence />;

  const situacaoAtiva = pj.situacao?.toUpperCase() === "ATIVA";

  return (
    <div className="mt-5 space-y-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      {/* HEADER / TÍTULO */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50">
            <HiBuildingOffice2 className="h-6 w-6 text-slate-600" />
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400">
              Receita Federal
            </div>
            <h2 className="text-xl font-semibold text-slate-900">
              Dados cadastrais PJ
            </h2>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge color="gray" className="w-fit">
            {formatDocument(pj.cnpj) || "CNPJ indisponível"}
          </Badge>
          {pj.situacao && (
            <Badge color={situacaoAtiva ? "success" : "failure"}>
              {pj.situacao}
            </Badge>
          )}
        </div>
      </div>

      {/* ================== DADOS CADASTRAIS ================== */}
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          <div className="rounded-lg border border-slate-200 bg-white px-2 py-1.5">
            <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-slate-500">
              Razão Social
            </p>
            <p className="mt-0.5 text-sm font-semibold text-slate-900">
              {pj.razaoSocial || "Indisponível"}
            </p>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white px-2 py-1.5">
            <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-slate-500">
              CNPJ
            </p>
            <p className="mt-0.5 text-sm font-semibold text-slate-900">
              {formatDocument(pj.cnpj) || "Indisponível"}
            </p>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white px-2 py-1.5">
            <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-slate-500">
              Natureza Jurídica
            </p>
            <p className="mt-0.5 text-sm text-slate-900">
              {pj.naturezaJuridica || "Indisponível"}
            </p>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white px-2 py-1.5">
            <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-slate-500">
              Data de Fundação
            </p>
            <div className="mt-0.5 flex items-center gap-1.5 text-sm text-slate-900">
              <HiCalendarDays className="text-slate-500" />
              <span>
                {pj.dataFundacao
                  ? moment(pj.dataFundacao).format("DD/MM/yyyy")
                  : "Indisponível"}
              </span>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white px-2 py-1.5">
            <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-slate-500">
              Capital Social
            </p>
            <div className="mt-0.5 flex items-center gap-1.5 text-sm text-slate-900">
              <HiCurrencyDollar className="text-slate-500" />
              <span>
                {formatToReal(pj.capitalSocial || pj.capitalInicial) ||
                  "Indisponível"}
              </span>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white px-2 py-1.5">
            <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-slate-500">
              Porte da Empresa
            </p>
            <p className="mt-0.5 text-sm text-slate-900">
              {pj.porteEmpresa || "Indisponível"}
            </p>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white px-2 py-1.5">
            <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-slate-500">
              Opção Simples
            </p>
            <div className="mt-0.5">
              {pj.opcaoSimples ? (
                <Badge className="w-fit" color="success">
                  Sim
                </Badge>
              ) : (
                <Badge className="w-fit" color="failure">
                  Não
                </Badge>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white px-2 py-1.5">
            <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-slate-500">
              Opção MEI
            </p>
            <div className="mt-0.5">
              {pj.opcaoMei ? (
                <Badge className="w-fit" color="success">
                  Sim
                </Badge>
              ) : (
                <Badge className="w-fit" color="failure">
                  Não
                </Badge>
              )}
            </div>
          </div>
        </div>

        <div className="mt-2 rounded-lg border border-slate-200 bg-white px-2 py-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex min-w-0 items-center gap-2">
              <HiMapPin className="h-4 w-4 text-slate-500" />
              <p className="text-sm text-slate-800">
                {fullAddress || "Indisponível"}
              </p>
            </div>
            <div className="ml-auto flex gap-2">
              <Button color="light" size="xs" onClick={handleCopyAddress}>
                <HiClipboard className="h-4 w-4" />
              </Button>

              {fullAddress && (
                <a href={mapsUrl} target="_blank" rel="noreferrer">
                  <Button color="light" size="xs">
                    <HiArrowTopRightOnSquare className="h-4 w-4" />
                  </Button>
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ================== SEÇÃO EXPANDIDA ================== */}
      {showMore && (
        <div className="animate-fadeIn space-y-6">
          {/* ================== SÓCIOS ================== */}
          {pj.socios && (
            <div>
              <TitleStripe>Sócios</TitleStripe>
              {pj.socios.length > 0 ? (
                <Sociedade socios={pj.socios} canFormatDocument={false} />
              ) : (
                <NoOcurrence />
              )}
            </div>
          )}
          {/* EMAILS */}
          {pj.email && (
            <div>
              <TitleStripe>E-mail</TitleStripe>
              <Emails emails={[{ email: pj.email }]} />
            </div>
          )}

          {/* TELEFONES */}
          {pj.telefones && (
            <div>
              <TitleStripe>Telefones</TitleStripe>
              {pj.telefones.length > 0 ? (
                <Telefones telefones={pj.telefones} />
              ) : (
                <NoOcurrence />
              )}
            </div>
          )}
        </div>
      )}

      {/* ================== BOTÃO "VER MAIS" ================== */}
      <div className="text-center pt-1">
        <Button
          color="light"
          onClick={() => setShowMore(!showMore)}
          className="mx-auto flex items-center justify-center gap-2 text-sm font-medium"
        >
          <div className="flex items-center gap-2">
            {showMore ? (
              <>
                <span>Ocultar detalhes </span>
                <HiChevronUp className="w-4 h-4" />
              </>
            ) : (
              <>
                <span>Ver mais informações </span>
                <HiChevronDown className="w-4 h-4" />
              </>
            )}
          </div>
        </Button>
      </div>
    </div>
  );
};

export default DadosReceitaFederalPJ;
